import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  AlertTriangle,
  Trophy,
  Edit3,
  CalendarRange,
  Plus,
  X,
  GripVertical,
  Lock,
  Copy,
  ClipboardPaste,
} from "lucide-react";
import {
  getProgram,
  updateProgram,
  getCoachingAthlete,
  getCoachingAthleteLactate,
  addProgramWeek,
  reorderProgramWeeks,
  getProgramWorkoutLogs,
} from "../../lib/api";
import {
  weekDateRange,
  formatDateRange,
  WORKOUT_TYPES,
  WORKOUT_TYPE_MAP,
  workoutTypeColor,
  parseRaceDistance,
} from "../../lib/coaching";
import {
  computeVdot,
  trainingPaces,
  lactateTrainingPaces,
  secToMinSec,
  workoutTypePaceKey,
} from "../../lib/vdot";
import { toast } from "sonner";
import { extractDurationOrDistance } from "../../lib/workout_title";

const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Auto-growing textarea — wraps overflow text onto subsequent lines and
 * grows the row vertically instead of bleeding into adjacent day cells.
 */
function AutoGrowTextarea({ value, onChange, className = "", autoFocus = false, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [value]);
  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={1}
      className={`resize-none overflow-hidden leading-snug ${className}`}
      {...rest}
    />
  );
}

// Lightweight planner workouts: only type + duration_or_distance + optional title.
// Stored on the same program.weeks[].days[].workouts[] structure as the WeekCanvas.
function newPlannerWorkout(type = "easy", text = "") {
  return {
    id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
    type,
    title: "",
    slot: "any",
    duration_or_distance: text,
    zones: [],
    rpe_min: null,
    rpe_max: null,
    structure: "",
    session_note: "",
    segments: [],
    strength_blocks: [],
  };
}

// Given a free-text cell value, infer the workout type from any keyword found
// anywhere in the text. Return the raw text UNMODIFIED as duration_or_distance
// (we never trim mid-typing — that would eat trailing spaces while the user types).
const KEYWORD_TYPE = [
  // More specific matches first
  ["race pace", "race_pace"],
  ["recovery", "recovery"],
  ["threshold", "threshold"],
  ["strength", "strength"],
  ["interval", "speed"],
  ["fartlek", "speed"],
  ["tempo", "tempo"],
  ["speed", "speed"],
  ["long", "long"],
  ["race", "race"],
  ["cross", "cross"],
  ["bike", "cross"],
  ["swim", "cross"],
  ["rest", "rest"],
  ["off", "rest"],
  ["easy", "easy"],
  ["lt", "threshold"],
  ["xt", "cross"],
  ["lift", "strength"],
];

function parseCellText(text) {
  if (text === null || text === undefined) return null;
  const raw = String(text);
  if (!raw) return null;
  const lower = raw.toLowerCase();
  for (const [kw, type] of KEYWORD_TYPE) {
    if (lower.includes(kw)) {
      return { type, duration_or_distance: raw };
    }
  }
  // No keyword detected → default to "easy" but still preserve the raw text
  return { type: "easy", duration_or_distance: raw };
}

function workoutShortLabel(workout) {
  if (!workout) return "";
  // Prefer the title: once a planner workout has been opened in the week
  // editor, its free text migrates to `title` and duration_or_distance
  // holds just the extracted quantity ("7 km"). The planner cell should
  // keep showing the full text either way.
  return workout.title || workout.duration_or_distance || "";
}

/**
 * Extract {meters, seconds} from a free-text workout line.
 * "10k" → 10000m; "60 min" → 3600s; "5 mi" → 8045m. Both can be non-zero.
 */
function parseWorkoutQuantity(text) {
  if (!text) return { meters: 0, seconds: 0 };
  const lower = String(text).toLowerCase();
  let meters = 0;
  let seconds = 0;
  for (const m of lower.matchAll(/(\d+(?:\.\d+)?)\s*(km|kilometers|kilometres|mi|miles|mile|m)\b/g)) {
    const v = parseFloat(m[1]);
    const u = m[2];
    if (u === "km" || u === "kilometers" || u === "kilometres") meters += v * 1000;
    else if (u === "mi" || u === "miles" || u === "mile") meters += v * 1609.344;
    else if (u === "m") meters += v;
  }
  // Catch "10K" patterns not followed by "m"
  for (const m of lower.matchAll(/(\d+(?:\.\d+)?)k(?!m)(?![a-z])/g)) {
    const v = parseFloat(m[1]);
    meters += v * 1000;
  }
  for (const m of lower.matchAll(/(\d+(?:\.\d+)?)\s*(hr|hour|hours|h)\b/g)) {
    seconds += parseFloat(m[1]) * 3600;
  }
  for (const m of lower.matchAll(/(\d+(?:\.\d+)?)\s*(min|minutes|mins)\b/g)) {
    seconds += parseFloat(m[1]) * 60;
  }
  // dedupe "5K" that matched both patterns: the /(\d+)k(?!m)/ pattern won't hit when followed by "m", so OK.
  return { meters, seconds };
}

// Default easy pace when no pace profile set: 6:30/km
const DEFAULT_EASY_SEC_PER_KM = 390;

/**
 * Get [slow, fast] sec/km for a workout type, using athlete paces if available.
 * Falls back to reasonable defaults around the default easy pace.
 */
function pacesForType(type, athletePaces) {
  const key = workoutTypePaceKey(type);
  if (athletePaces && key && athletePaces[key]) {
    return [athletePaces[key].slow_sec_per_km, athletePaces[key].fast_sec_per_km];
  }
  // Defaults — bracket around easy, roughly Daniels %diffs
  if (!key || key === "easy") return [DEFAULT_EASY_SEC_PER_KM + 30, DEFAULT_EASY_SEC_PER_KM - 30];
  if (key === "marathon") return [DEFAULT_EASY_SEC_PER_KM - 30, DEFAULT_EASY_SEC_PER_KM - 70];
  if (key === "threshold") return [DEFAULT_EASY_SEC_PER_KM - 70, DEFAULT_EASY_SEC_PER_KM - 110];
  if (key === "interval") return [DEFAULT_EASY_SEC_PER_KM - 110, DEFAULT_EASY_SEC_PER_KM - 150];
  if (key === "repetition") return [DEFAULT_EASY_SEC_PER_KM - 150, DEFAULT_EASY_SEC_PER_KM - 180];
  return [DEFAULT_EASY_SEC_PER_KM + 30, DEFAULT_EASY_SEC_PER_KM - 30];
}

/**
 * Unified km + minutes for a week, using athlete-specific pace to cross-convert.
 * Each workout contributes both km and minutes even if only one was given.
 * Optionally adds race contributions (parsed from race.distance) for races
 * that fall in the week's date range.
 */
function unifiedWeekVolume(week, athletePaces, racesInWeek = []) {
  if (!week) return { km: 0, minutes: 0 };
  let km = 0;
  let minutes = 0;
  for (const day of week.days || []) {
    for (const w of day.workouts || []) {
      if (w.type === "rest" || w.type === "strength") continue;
      const q = parseWorkoutQuantity(w.duration_or_distance);
      const [paceSlow, paceFast] = pacesForType(w.type, athletePaces);
      const pace = (paceSlow + paceFast) / 2; // midpoint sec/km
      if (q.meters > 0) {
        const distKm = q.meters / 1000;
        km += distKm;
        minutes += (distKm * pace) / 60; // estimated minutes from distance
      } else if (q.seconds > 0) {
        const mins = q.seconds / 60;
        minutes += mins;
        km += (q.seconds / pace); // estimated km from minutes
      }
    }
  }
  // Race contribution — uses shared parseRaceDistance helper so a bare
  // number (e.g. "28") is read as 28 km, common aliases work, and time
  // is estimated at the athlete's race pace.
  for (const r of racesInWeek) {
    const parsed = parseRaceDistance(r.distance || r.name, athletePaces);
    if (parsed.km > 0) {
      km += parsed.km;
      minutes += parsed.minutes;
    }
  }
  return { km, minutes };
}

function formatUnifiedVolume({ km, minutes }) {
  const parts = [];
  if (km > 0.1) parts.push(`${km < 10 ? km.toFixed(1) : Math.round(km)} km`);
  if (minutes > 0.5) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    parts.push(h > 0 ? `${h}h ${m}m` : `${m}m`);
  }
  return parts.join(" · ");
}

/**
 * Per-workout time estimate string (e.g. "~50–56 min") when the workout has distance but no explicit duration.
 */
function workoutTimeEstimate(workout, athletePaces) {
  if (!workout || workout.type === "rest" || workout.type === "strength") return "";
  const q = parseWorkoutQuantity(workout.duration_or_distance);
  if (q.seconds > 0) return ""; // user already gave duration
  if (q.meters <= 0) return "";
  const [paceSlow, paceFast] = pacesForType(workout.type, athletePaces);
  const km = q.meters / 1000;
  const tSlow = Math.round((km * paceSlow) / 60);
  const tFast = Math.round((km * paceFast) / 60);
  if (tSlow === tFast) return `~${tSlow}m`;
  return `~${tFast}–${tSlow}m`;
}

function normalize(text) {
  return (text || "").trim().toLowerCase();
}

function deltaPctClass(curr, prev) {
  if (!prev || prev <= 0 || !curr) return null;
  const delta = (curr - prev) / prev;
  if (delta > 0.1) return { delta, cls: "text-[var(--tir-burgundy)]", icon: true };
  if (delta < -0.15) return { delta, cls: "text-[var(--tir-navy)]", icon: false };
  return { delta, cls: "text-[var(--tir-muted)]", icon: false };
}

export default function ProgramPlannerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [athleteLactate, setAthleteLactate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Newest-added workout id — its textarea will autofocus on render.
  const [focusKey, setFocusKey] = useState(null);
  // Native HTML5 drag state — { weekIdx, dayIdx, workoutId } during drag.
  const [dragInfo, setDragInfo] = useState(null);
  const [dragOver, setDragOver] = useState(null); // "wi-di"
  // Copy/paste clipboards. Single workout OR full-week (7 days × workouts).
  // null when nothing has been copied yet.
  const [clipWorkout, setClipWorkout] = useState(null);
  const [clipWeek, setClipWeek] = useState(null);
  // Right-click context menu on workout chips.
  // { x, y, weekIdx, dayIdx, workoutIdx, workoutId } | null
  const [ctxMenu, setCtxMenu] = useState(null);
  // Week-row drag-to-reorder state. Holds the week_number being dragged
  // (or `null` when no drag is active).
  const [draggingWeekNum, setDraggingWeekNum] = useState(null);
  const [dropTargetWeekNum, setDropTargetWeekNum] = useState(null);
  // Aggregated actual volume per week — { week_number: {km, minutes} }.
  // Rendered as a small footer under each week's Copy/Paste controls so
  // the coach can adjust the plan with big-picture actuals in view.
  const [volByWeek, setVolByWeek] = useState({});
  const saveTimer = useRef(null);

  const onAddWeek = async () => {
    if (!program) return;
    try {
      if (dirty) await persist();
      const updated = await addProgramWeek(id);
      setProgram(updated);
      toast.success(
        `Week ${updated.weeks.length} added — drag the row to position it`,
        { duration: 2400 }
      );
    } catch {
      toast.error("Couldn't add week");
    }
  };

  const moveWeekTo = async (sourceWeekNum, destIndex) => {
    if (!program) return;
    const order = program.weeks.map((w) => w.week_number);
    const fromIdx = order.indexOf(sourceWeekNum);
    if (fromIdx < 0) return;
    const safeDest = Math.max(0, Math.min(destIndex, order.length - 1));
    if (fromIdx === safeDest) return;
    const next = [...order];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(safeDest, 0, moved);
    // Optimistic UI update
    setProgram((p) => {
      if (!p) return p;
      const reordered = next.map((origNum, i) => {
        const w = p.weeks.find((x) => x.week_number === origNum);
        return { ...w, week_number: i + 1 };
      });
      return { ...p, weeks: reordered, total_weeks: reordered.length };
    });
    try {
      const fresh = await reorderProgramWeeks(id, next);
      setProgram(fresh);
      toast.success("Week order updated");
    } catch {
      toast.error("Couldn't reorder — refreshing");
      try {
        const p = await getProgram(id);
        setProgram(p);
      } catch {
        /* swallow */
      }
    }
  };

  // Close the context menu on any outside interaction or Escape.
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await getProgram(id);
        if (!alive) return;
        setProgram(p);
        if (p.athlete_id) {
          try {
            const a = await getCoachingAthlete(p.athlete_id);
            if (alive) setAthlete(a);
          } catch {
            /* ignore */
          }
          try {
            const lac = await getCoachingAthleteLactate(p.athlete_id);
            if (alive) setAthleteLactate(lac || null);
          } catch {
            if (alive) setAthleteLactate(null);
          }
          // Workout logs — aggregate per-week actual volume so the planner
          // can render a "Logged X km / Y planned · adherence%" footer
          // next to each week's Copy/Paste controls. Only count complete
          // and partial (missed adds 0 to volume).
          try {
            const logs = await getProgramWorkoutLogs(id);
            if (alive) {
              const volByWeek = {};
              for (const l of logs || []) {
                if (!l?.status) continue;
                if (l.status !== "complete" && l.status !== "partial") continue;
                const wk = String(l.week_number);
                if (!volByWeek[wk]) volByWeek[wk] = { km: 0, minutes: 0 };
                volByWeek[wk].km += Number(l.distance_actual_km) || 0;
                volByWeek[wk].minutes += Number(l.duration_actual_min) || 0;
              }
              setVolByWeek(volByWeek);
            }
          } catch {
            /* footer just won't render — non-blocking */
          }
        }
      } catch {
        toast.error("Couldn't load program");
        navigate("/coaching");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  // Autosave (debounced 1.5s)
  useEffect(() => {
    if (!dirty || !program) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await persist();
    }, 1500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, program]);

  const persist = async () => {
    if (!program) return;
    setSaving(true);
    try {
      await updateProgram(id, {
        title: program.title,
        goal: program.goal,
        target_race: program.target_race,
        experience_level: program.experience_level,
        max_mileage: program.max_mileage,
        prerequisite: program.prerequisite,
        total_weeks: program.total_weeks,
        start_day: program.start_day,
        start_date: program.start_date,
        show_total_volume: program.show_total_volume,
        intro_howto: program.intro_howto,
        intro_modify: program.intro_modify,
        weeks: program.weeks,
      });
      // Intentionally do NOT setProgram(updated) — the user may have typed
      // additional characters while the save was in flight. Setting state
      // would stomp those in-flight edits (and reset cursor positions).
      setDirty(false);
    } catch {
      toast.error("Couldn't save planner");
    } finally {
      setSaving(false);
    }
  };

  const updateWeek = (idx, patch) => {
    setProgram((p) => ({
      ...p,
      weeks: p.weeks.map((w, i) => (i === idx ? { ...w, ...patch } : w)),
    }));
    setDirty(true);
  };

  const updateDayWorkoutText = (weekIdx, dayIdx, workoutIdx, text) => {
    setProgram((p) => {
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx) return w;
        const days = (w.days || []).map((d, di) => {
          if (di !== dayIdx) return d;
          const workouts = [...(d.workouts || [])];
          if (!text || !text.trim()) {
            // Remove this workout slot when cleared
            workouts.splice(workoutIdx, 1);
            return { ...d, workouts };
          }
          const parsed = parseCellText(text);
          if (workoutIdx < workouts.length) {
            const existing = workouts[workoutIdx];
            // Titled workouts (migrated by the week editor) keep the full
            // text in `title` and only the extracted quantity in duration.
            const hasTitle = !!String(existing.title || "").trim();
            workouts[workoutIdx] = hasTitle
              ? {
                  ...existing,
                  type: parsed.type,
                  title: parsed.duration_or_distance,
                  duration_or_distance:
                    extractDurationOrDistance(parsed.duration_or_distance) || "",
                }
              : {
                  ...existing,
                  type: parsed.type,
                  duration_or_distance: parsed.duration_or_distance,
                };
          } else {
            // Append new
            workouts.push(newPlannerWorkout(parsed.type, parsed.duration_or_distance));
          }
          return { ...d, workouts };
        });
        return { ...w, days };
      });
      return { ...p, weeks };
    });
    setDirty(true);
  };

  const addDayWorkout = (weekIdx, dayIdx) => {
    const newWorkout = newPlannerWorkout("easy", "");
    setProgram((p) => {
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx) return w;
        const days = (w.days || []).map((d, di) => {
          if (di !== dayIdx) return d;
          return {
            ...d,
            workouts: [...(d.workouts || []), newWorkout],
          };
        });
        return { ...w, days };
      });
      return { ...p, weeks };
    });
    setDirty(true);
    setFocusKey(newWorkout.id);
  };

  // Move a workout between cells via native HTML5 drag-and-drop.
  // Source and dest are { weekIdx, dayIdx, workoutId } / { weekIdx, dayIdx }.
  const moveWorkout = (src, dest) => {
    if (!src || !dest) return;
    if (src.weekIdx === dest.weekIdx && src.dayIdx === dest.dayIdx) return;
    setProgram((p) => {
      const weeks = p.weeks.map((w, wi) => {
        if (wi !== src.weekIdx && wi !== dest.weekIdx) return w;
        return { ...w, days: (w.days || []).map((d) => ({ ...d, workouts: [...(d.workouts || [])] })) };
      });
      // Remove from source
      const srcDay = weeks[src.weekIdx].days[src.dayIdx];
      const idx = srcDay.workouts.findIndex((x) => x.id === src.workoutId);
      if (idx < 0) return p;
      const [moved] = srcDay.workouts.splice(idx, 1);
      // Append to destination
      const destDay = weeks[dest.weekIdx].days[dest.dayIdx];
      destDay.workouts.push(moved);
      return { ...p, weeks };
    });
    setDirty(true);
  };

  const removeDayWorkout = (weekIdx, dayIdx, workoutIdx) => {
    setProgram((p) => {
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx) return w;
        const days = (w.days || []).map((d, di) => {
          if (di !== dayIdx) return d;
          const workouts = [...(d.workouts || [])];
          workouts.splice(workoutIdx, 1);
          return { ...d, workouts };
        });
        return { ...w, days };
      });
      return { ...p, weeks };
    });
    setDirty(true);
  };

  // ---- Copy / paste ----
  // Re-id a workout (and any nested ids) so paste creates a fresh instance
  // instead of duplicating an existing id (which would confuse drag/drop and
  // log keys downstream).
  const cloneWorkoutFresh = (wo) => {
    const fresh = JSON.parse(JSON.stringify(wo));
    fresh.id = `tmp-${Math.random().toString(36).slice(2, 10)}`;
    if (Array.isArray(fresh.segments)) {
      fresh.segments = fresh.segments.map((s) => {
        const ns = { ...s, id: `tmp-${Math.random().toString(36).slice(2, 10)}` };
        if (Array.isArray(s.items)) {
          ns.items = s.items.map((it) => ({
            ...it,
            id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
          }));
        }
        return ns;
      });
    }
    return fresh;
  };

  const copyWorkout = (wo) => {
    setClipWorkout(cloneWorkoutFresh(wo));
    setClipWeek(null);
    toast.success("Workout copied — paste into any day", { duration: 1800 });
  };

  const pasteWorkoutInto = (weekIdx, dayIdx) => {
    if (!clipWorkout) return;
    const fresh = cloneWorkoutFresh(clipWorkout);
    setProgram((p) => {
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx) return w;
        const days = (w.days || []).map((d, di) => {
          if (di !== dayIdx) return d;
          return { ...d, workouts: [...(d.workouts || []), fresh] };
        });
        return { ...w, days };
      });
      return { ...p, weeks };
    });
    setDirty(true);
  };

  const copyWeek = (weekIdx) => {
    setProgram((p) => {
      const w = p.weeks[weekIdx];
      // Snapshot: phase, recovery flag, coach notes, and 7 days × workouts.
      const snapshot = {
        phase_label: w.phase_label || "",
        is_recovery: !!w.is_recovery,
        coach_note: w.coach_note || "",
        coach_private_note: w.coach_private_note || "",
        days: (w.days || []).slice(0, 7).map((d) => ({
          label: d.label,
          coach_note: d.coach_note || "",
          workouts: (d.workouts || []).map(cloneWorkoutFresh),
        })),
      };
      setClipWeek(snapshot);
      setClipWorkout(null);
      return p;
    });
    toast.success(`Week ${program.weeks[weekIdx].week_number} copied — paste into any other week`, {
      duration: 1800,
    });
  };

  const pasteWeekInto = (weekIdx) => {
    if (!clipWeek) return;
    setProgram((p) => {
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx) return w;
        const newDays = (w.days || []).map((d, di) => {
          const src = clipWeek.days[di];
          if (!src) return d;
          return {
            ...d,
            coach_note: src.coach_note || "",
            workouts: src.workouts.map(cloneWorkoutFresh),
          };
        });
        return {
          ...w,
          phase_label: clipWeek.phase_label,
          is_recovery: clipWeek.is_recovery,
          coach_note: clipWeek.coach_note,
          coach_private_note: clipWeek.coach_private_note,
          days: newDays,
        };
      });
      return { ...p, weeks };
    });
    setDirty(true);
    toast.success(`Pasted into Week ${program.weeks[weekIdx].week_number}`, { duration: 1500 });
  };

  // Open the full week editor for a given week, persisting first if dirty.
  const openWeekEditor = async (weekNumber) => {
    if (dirty) {
      await persist();
    }
    navigate(`/coaching/programs/${id}/edit?week=${weekNumber}`);
  };

  // Duplicate a workout in place (appends a fresh-id clone to the same day).
  const duplicateWorkout = (weekIdx, dayIdx, workoutIdx) => {
    setProgram((p) => {
      const wo = p.weeks[weekIdx]?.days?.[dayIdx]?.workouts?.[workoutIdx];
      if (!wo) return p;
      const fresh = cloneWorkoutFresh(wo);
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx) return w;
        const days = (w.days || []).map((d, di) => {
          if (di !== dayIdx) return d;
          const workouts = [...(d.workouts || [])];
          workouts.splice(workoutIdx + 1, 0, fresh);
          return { ...d, workouts };
        });
        return { ...w, days };
      });
      return { ...p, weeks };
    });
    setDirty(true);
  };

  // Move a workout to the same weekday in the next week (creates the
  // workout slot there). No-op on the last week.
  const moveToNextWeek = (weekIdx, dayIdx, workoutIdx) => {
    if (!program || weekIdx >= program.weeks.length - 1) {
      toast.error("Already on the last week");
      return;
    }
    setProgram((p) => {
      const srcDay = p.weeks[weekIdx]?.days?.[dayIdx];
      const wo = srcDay?.workouts?.[workoutIdx];
      if (!wo) return p;
      const weeks = p.weeks.map((w, i) => {
        if (i !== weekIdx && i !== weekIdx + 1) return w;
        return { ...w, days: (w.days || []).map((d) => ({ ...d, workouts: [...(d.workouts || [])] })) };
      });
      // Remove from source
      weeks[weekIdx].days[dayIdx].workouts.splice(workoutIdx, 1);
      // Append to next week, same weekday
      const dest = weeks[weekIdx + 1].days[dayIdx];
      if (dest) dest.workouts.push(wo);
      return { ...p, weeks };
    });
    setDirty(true);
  };

  const races = useMemo(() => {
    const list = (athlete?.races || []).filter((r) => r.date);
    return list
      .map((r) => ({ ...r }))
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [athlete?.races]);

  const racesByDate = useMemo(() => {
    const m = {};
    for (const r of races) m[r.date] = r;
    return m;
  }, [races]);

  // Compute the race within a week's date range
  function raceInWeek(week) {
    if (!program?.start_date) return null;
    const [ws, we] = weekDateRange(program.start_date, week.week_number);
    if (!ws || !we) return null;
    return races.find((r) => r.date >= ws && r.date <= we) || null;
  }

  // Race-back: snap program start_date to N weeks before the A-priority race.
  const setStartFromRace = (race) => {
    if (!race?.date || !program) return;
    const totalWeeks = program.total_weeks || program.weeks.length || 12;
    const raceDate = new Date(race.date + "T00:00:00");
    if (isNaN(raceDate.getTime())) return;
    const start = new Date(raceDate);
    start.setDate(raceDate.getDate() - (totalWeeks - 1) * 7);
    // Snap to Monday
    const dow = start.getDay(); // 0=Sun
    const offsetToMon = (dow === 0 ? -6 : 1 - dow);
    start.setDate(start.getDate() + offsetToMon);
    const iso = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
    setProgram((p) => ({ ...p, start_date: iso }));
    setDirty(true);
    toast.success(
      `Start date set to ${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${totalWeeks} weeks before ${race.name}`
    );
  };

  // Auto-recovery: every 4th week marked recovery
  const applyAutoRecovery = () => {
    setProgram((p) => ({
      ...p,
      weeks: p.weeks.map((w, i) => ({ ...w, is_recovery: (i + 1) % 4 === 0 })),
    }));
    setDirty(true);
    toast.success("Marked every 4th week as recovery");
  };

  // Pace prescription source: lactate test data (if available) trumps VDOT.
  // Both produce the same shape, so existing helpers consume them identically.
  const athletePaces = useMemo(() => {
    const lacPaces = lactateTrainingPaces(athleteLactate);
    if (lacPaces) return lacPaces;
    const pp = athlete?.pace_profile;
    if (!pp) return null;
    const vdot = computeVdot(pp.race_distance_m, pp.race_time_seconds);
    return trainingPaces(vdot);
  }, [athleteLactate, athlete?.pace_profile]);
  const usingLactatePaces = !!lactateTrainingPaces(athleteLactate);

  if (loading || !program) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading planner…
      </div>
    );
  }

  const backLink = program.athlete_id
    ? `/coaching/athletes/${program.athlete_id}`
    : "/coaching/library";

  // Helper: list races within a given week's date range
  const racesInWeekRange = (week) => {
    if (!program?.start_date) return [];
    const [ws, we] = weekDateRange(program.start_date, week.week_number);
    if (!ws || !we) return [];
    return races.filter((r) => r.date >= ws && r.date <= we);
  };

  // Unified volume per week, factoring in athlete's paces and any A/B/C race in the week
  const weekVolumes = program.weeks.map((w) =>
    unifiedWeekVolume(w, athletePaces, racesInWeekRange(w))
  );
  const maxKm = Math.max(1, ...weekVolumes.map((v) => v.km || 0));
  const maxMin = Math.max(1, ...weekVolumes.map((v) => v.minutes || 0));

  return (
    <div
      className="max-w-full mx-auto px-3 sm:px-4 py-6"
      data-testid="program-planner-page"
    >
      <Link
        to={backLink}
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-3"
      >
        <ArrowLeft size={14} /> {athlete ? athlete.name : "Library"}
      </Link>

      <header className="border-b border-[var(--tir-ink)] pb-5 mb-6 flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
            Coaching · Program planner
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--tir-ink)] leading-tight">
            {program.title}
          </h1>
          <div className="text-xs text-[var(--tir-muted)] mt-1">
            High-level outline — types and totals only. Click "Open week" to add zones, segments, and full detail.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]" data-testid="planner-save-status">
            {saving ? "Saving…" : dirty ? "Unsaved" : "Saved"}
          </div>
          <button
            type="button"
            onClick={onAddWeek}
            data-testid="planner-add-week-btn"
            title="Add a new week to the end. Drag the row's week badge to reposition it."
            className="inline-flex items-center gap-2 border border-[var(--tir-border)] bg-white px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold text-[var(--tir-ink)] hover:border-[var(--tir-ink)] hover:bg-[var(--tir-ivory)] transition-colors"
          >
            <Plus size={13} /> Add week
          </button>
          <button
            type="button"
            onClick={persist}
            disabled={!dirty || saving}
            data-testid="planner-save-btn"
            className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-40"
          >
            <Save size={13} /> Save
          </button>
        </div>
      </header>

      {/* Quick controls */}
      <section className="mb-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-3">
          <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
            Start date
          </label>
          <input
            type="date"
            value={program.start_date || ""}
            onChange={(e) => {
              setProgram({ ...program, start_date: e.target.value || null });
              setDirty(true);
            }}
            data-testid="planner-start-date"
            className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none font-mono-tir"
          />
        </div>
        <div className="md:col-span-4">
          <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
            Race-back from
          </label>
          {races.length === 0 ? (
            <div className="text-[11px] italic text-[var(--tir-muted)] py-2">
              No races scheduled yet.{" "}
              {athlete && (
                <Link
                  to={`/coaching/athletes/${athlete.id}`}
                  className="underline hover:text-[var(--tir-burgundy)]"
                >
                  Add one →
                </Link>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {races
                .filter((r) => r.priority === "A")
                .slice(0, 3)
                .map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setStartFromRace(r)}
                    data-testid={`raceback-btn-${r.id}`}
                    className="inline-flex items-center gap-1.5 border border-[var(--tir-burgundy)] text-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)] hover:text-white px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold transition-colors"
                  >
                    <Trophy size={11} /> {r.name}
                  </button>
                ))}
              {races.filter((r) => r.priority === "A").length === 0 && (
                <span className="text-[11px] italic text-[var(--tir-muted)] py-2">
                  No A-priority races
                </span>
              )}
            </div>
          )}
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
            Recovery rhythm
          </label>
          <button
            type="button"
            onClick={applyAutoRecovery}
            data-testid="auto-recovery-btn"
            className="inline-flex items-center gap-1.5 border border-[var(--tir-border)] hover:border-[var(--tir-navy)] text-[var(--tir-navy)] px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold transition-colors"
          >
            <CalendarRange size={11} /> Mark every 4th week
          </button>
        </div>
        <div className="md:col-span-2 text-right flex items-end justify-end gap-6">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
              Done editing
            </div>
            <div
              className="font-mono-tir text-2xl text-[#2f7d4a]"
              data-testid="planner-done-count"
            >
              {program.weeks.filter((w) => w.planning_complete).length}
              <span className="text-[var(--tir-muted)] text-base">
                /{program.weeks.length}
              </span>
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
              Total weeks
            </div>
            <div className="font-mono-tir text-2xl text-[var(--tir-ink)]">
              {program.weeks.length}
            </div>
          </div>
        </div>
      </section>

      {/* The big planner table — extends edge-to-edge on mobile so the
          horizontal scroll has the full viewport to work with. On md+ we
          drop the overflow wrapper entirely so the <thead>'s `sticky top`
          actually sticks to the page viewport (overflow-x:auto on a
          parent forces overflow-y:auto and breaks sticky on the page).
          On mobile we still need the horizontal scroll, so the wrapper
          stays — the sticky header won't pin there, but the column
          width is the priority on small screens. */}
      <div
        className="border border-[var(--tir-border)] bg-white overflow-x-auto md:overflow-visible -mx-3 sm:mx-0 sm:rounded-none"
        data-testid="planner-table-wrapper"
      >
        {/* Mobile-only hint: this table scrolls horizontally */}
        <div className="md:hidden text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] italic px-3 py-1.5 border-b border-[var(--tir-border)] bg-[var(--tir-ivory)]">
          Tip: swipe horizontally to see all days
        </div>
        <table
          className="text-[11px] border-collapse table-fixed w-full min-w-[1080px]"
        >
          <colgroup>
            <col style={{ width: "108px" }} />
            <col style={{ width: "70px" }} />
            {DAY_KEYS.map((d) => (
              <col key={d} style={{ width: "98px" }} />
            ))}
            <col style={{ width: "62px" }} />
            <col style={{ width: "34px" }} />
            <col style={{ width: "200px" }} />
            <col style={{ width: "120px" }} />
          </colgroup>
          <thead className="bg-[var(--tir-ivory)] border-b border-[var(--tir-ink)] sticky top-16 z-30 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
            <tr className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] font-semibold">
              <th className="text-left py-2 px-1.5 sticky left-0 bg-[var(--tir-ivory)] z-10 border-r border-[var(--tir-border)]">
                Week
              </th>
              <th className="text-left py-2 px-1.5 bg-[var(--tir-ivory)]">Phase</th>
              {DAY_KEYS.map((d) => (
                <th key={d} className="text-left py-2 px-1.5 bg-[var(--tir-ivory)]">
                  {d}
                </th>
              ))}
              <th className="text-right py-2 px-1.5 bg-[var(--tir-ivory)]">Vol</th>
              <th className="text-center py-2 px-1 bg-[var(--tir-ivory)]">Δ</th>
              <th className="text-left py-2 px-1.5 bg-[var(--tir-ivory)]">Notes</th>
              <th className="text-left py-2 px-1.5 bg-[var(--tir-ivory)]" title="Private to you — never shown to athlete">
                <span className="inline-flex items-center gap-1">
                  <Lock size={9} /> Private
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {program.weeks.map((w, wi) => {
              const [ws, we] = weekDateRange(program.start_date, w.week_number);
              const race = raceInWeek(w);
              const vol = weekVolumes[wi];
              // The most recent prior week. Used for the small "▼ N%"
              // chip in the legend (any decrease vs. last week is fine).
              const prevVol = wi > 0 ? weekVolumes[wi - 1] : null;
              // For *positive* week-over-week jumps we want a meaningful
              // comparison: skip backwards past any recovery weeks (the dip
              // is intentional, so bouncing back up is not a "true" build).
              // Find the most recent non-recovery week before this one.
              let baselineVol = prevVol;
              for (let j = wi - 1; j >= 0; j -= 1) {
                const wPrev = program.weeks[j];
                if (!wPrev?.is_recovery) {
                  baselineVol = weekVolumes[j];
                  break;
                }
                baselineVol = null; // every prior week was recovery
              }
              // Use the recovery-aware baseline when comparing UP, otherwise
              // keep the immediate previous week so a real drop after a peak
              // still shows as a decrease.
              const curKm = vol.km || 0;
              const baselineKm = baselineVol?.km;
              const prevKm = prevVol?.km;
              const compareKm =
                baselineKm != null && curKm >= (prevKm ?? 0) ? baselineKm : prevKm;
              const dKm = deltaPctClass(curKm, compareKm);
              const isRecovery = !!w.is_recovery;
              const isDone = !!w.planning_complete;
              const rowBg = isDone
                ? "bg-[#eaf3ec]"
                : isRecovery
                ? "bg-[#e8eef2]"
                : race
                ? "bg-[#fdf6e7]"
                : wi % 2 === 0
                ? "bg-white"
                : "bg-[var(--tir-ivory)]/40";
              const phase = w.phase_label || "";
              return (
                <tr
                  key={w.id}
                  className={`border-b border-[var(--tir-border)] align-top ${rowBg} ${
                    draggingWeekNum === w.week_number ? "opacity-40" : ""
                  } ${
                    dropTargetWeekNum === w.week_number && draggingWeekNum !== w.week_number
                      ? "outline outline-2 outline-[var(--tir-burgundy)]"
                      : ""
                  }`}
                  data-testid={`planner-row-${w.week_number}`}
                  onDragOver={(e) => {
                    if (draggingWeekNum != null && draggingWeekNum !== w.week_number) {
                      e.preventDefault();
                      setDropTargetWeekNum(w.week_number);
                    }
                  }}
                  onDragLeave={() => {
                    if (dropTargetWeekNum === w.week_number) setDropTargetWeekNum(null);
                  }}
                  onDrop={(e) => {
                    if (draggingWeekNum == null || draggingWeekNum === w.week_number) return;
                    e.preventDefault();
                    const src = draggingWeekNum;
                    setDraggingWeekNum(null);
                    setDropTargetWeekNum(null);
                    // The visual row index `wi` is also the desired final
                    // 0-based slot for the dropped week.
                    moveWeekTo(src, wi);
                  }}
                >
                  <td className="px-1.5 py-2 sticky left-0 bg-inherit z-[1] border-r border-[var(--tir-border)]">
                    <div
                      className="flex items-center justify-between gap-1"
                      draggable
                      onDragStart={(e) => {
                        setDraggingWeekNum(w.week_number);
                        e.dataTransfer.effectAllowed = "move";
                        try {
                          e.dataTransfer.setData("text/plain", `week-${w.week_number}`);
                        } catch { /* ignore */ }
                      }}
                      onDragEnd={() => {
                        setDraggingWeekNum(null);
                        setDropTargetWeekNum(null);
                      }}
                      title="Drag this week to reorder"
                      data-testid={`planner-week-drag-${w.week_number}`}
                    >
                      <div className="flex items-center gap-1 cursor-grab active:cursor-grabbing">
                        <GripVertical
                          size={11}
                          className="text-[var(--tir-muted)]/50"
                          aria-hidden="true"
                        />
                        <label
                          className="inline-flex items-center cursor-pointer"
                          title={isDone ? "Week marked done — click to unmark" : "Mark this week as done (your planning, not the athlete)"}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onDragStart={(e) => e.preventDefault()}
                        >
                          <input
                            type="checkbox"
                            checked={isDone}
                            onChange={(e) =>
                              updateWeek(wi, { planning_complete: e.target.checked })
                            }
                            data-testid={`planner-done-${w.week_number}`}
                            className="h-3.5 w-3.5 accent-[#2f7d4a] cursor-pointer"
                          />
                        </label>
                        <div className={`font-serif-display text-base leading-tight ${
                          isDone ? "text-[#2f7d4a]" : "text-[var(--tir-ink)]"
                        }`}>
                          W{w.week_number}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openWeekEditor(w.week_number)}
                        onDragStart={(e) => e.preventDefault()}
                        data-testid={`planner-open-week-${w.week_number}`}
                        title="Open full week editor"
                        className="inline-flex items-center gap-0.5 text-[9px] tracking-[0.18em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] font-semibold border border-[var(--tir-burgundy)]/50 hover:border-[var(--tir-burgundy)] px-1.5 py-0.5"
                      >
                        <Edit3 size={9} /> Open
                      </button>
                    </div>
                    {ws && we && (
                      <div className="text-[9px] tracking-[0.1em] text-[var(--tir-muted)] font-mono-tir mt-0.5">
                        {formatDateRange(ws, we)}
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap gap-1">
                      <label className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-[var(--tir-navy)] font-semibold cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isRecovery}
                          onChange={(e) =>
                            updateWeek(wi, { is_recovery: e.target.checked })
                          }
                          data-testid={`planner-recovery-${w.week_number}`}
                          className="h-3 w-3 accent-[var(--tir-navy)]"
                        />
                        Recovery
                      </label>
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copyWeek(wi)}
                        data-testid={`planner-copy-week-${w.week_number}`}
                        title="Copy this whole week"
                        className="inline-flex items-center gap-0.5 text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] font-semibold"
                      >
                        <Copy size={9} /> Copy
                      </button>
                      {clipWeek && (
                        <button
                          type="button"
                          onClick={() => pasteWeekInto(wi)}
                          data-testid={`planner-paste-week-${w.week_number}`}
                          title="Paste copied week here (overwrites this week)"
                          className="inline-flex items-center gap-0.5 text-[9px] tracking-[0.16em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] font-semibold border border-dashed border-[var(--tir-burgundy)]/50 hover:border-[var(--tir-burgundy)] px-1 py-px"
                        >
                          <ClipboardPaste size={9} /> Paste
                        </button>
                      )}
                    </div>
                    {(() => {
                      // Planned-vs-actual footer.
                      //
                      // - Shows when the athlete has logged ANY volume for
                      //   the week (the coach already sees planned volume
                      //   in the existing pill at the top of the row, so
                      //   we only surface the comparison once there's
                      //   something to compare against).
                      // - Renders `Logged 42 km / 48 km · 88%` style so the
                      //   coach can spot adherence + absolute volume at a
                      //   glance while editing the plan.
                      if (!program?.athlete_id) return null;
                      const vol = volByWeek[String(w.week_number)];
                      if (!vol) return null;
                      const aKm = Number(vol.km) || 0;
                      const aMin = Number(vol.minutes) || 0;
                      if (aKm <= 0.05 && aMin <= 0.5) return null;
                      const planned = weekVolumes[wi] || { km: 0, minutes: 0 };
                      const pKm = Number(planned.km) || 0;
                      const pMin = Number(planned.minutes) || 0;
                      const aHrs = aMin / 60;
                      const pHrs = pMin / 60;
                      // Prefer km adherence when there's planned km, else
                      // fall back to time adherence.
                      let adherencePct = null;
                      if (pKm > 0.05) adherencePct = (aKm / pKm) * 100;
                      else if (pMin > 0.5) adherencePct = (aMin / pMin) * 100;
                      const pctColor =
                        adherencePct == null
                          ? "text-[var(--tir-muted)]"
                          : adherencePct >= 95 && adherencePct <= 110
                          ? "text-emerald-700"
                          : adherencePct >= 80
                          ? "text-amber-700"
                          : "text-[var(--tir-burgundy)]";
                      const fmtKm = (k) => (k >= 10 ? Math.round(k) : k.toFixed(1));
                      const fmtTime = (mn) => {
                        const h = mn / 60;
                        return h >= 1 ? `${h.toFixed(1)} h` : `${Math.round(mn)} min`;
                      };
                      return (
                        <div
                          className="mt-1 text-[10px] leading-tight"
                          data-testid={`planner-week-actuals-${w.week_number}`}
                          title="Athlete's logged volume vs the planned volume for this week (complete + partial)"
                        >
                          <span className="font-semibold text-[var(--tir-ink)] tracking-[0.16em] uppercase">
                            Logged
                          </span>
                          {aKm > 0.05 && (
                            <div className="font-mono-tir mt-0.5 text-[11px]">
                              <span
                                className="text-[var(--tir-ink)]"
                                data-testid={`planner-week-actual-km-${w.week_number}`}
                              >
                                {fmtKm(aKm)} km
                              </span>
                              {pKm > 0.05 && (
                                <span className="text-[var(--tir-muted)]">
                                  {" / "}
                                  {fmtKm(pKm)} km
                                </span>
                              )}
                            </div>
                          )}
                          {aMin > 0.5 && (
                            <div className="font-mono-tir text-[11px]">
                              <span
                                className="text-[var(--tir-ink)]"
                                data-testid={`planner-week-actual-time-${w.week_number}`}
                              >
                                {aHrs >= 1 ? `${aHrs.toFixed(1)} h` : `${Math.round(aMin)} min`}
                              </span>
                              {pMin > 0.5 && (
                                <span className="text-[var(--tir-muted)]">
                                  {" / "}
                                  {pHrs >= 1
                                    ? `${pHrs.toFixed(1)} h`
                                    : `${Math.round(pMin)} min`}
                                </span>
                              )}
                            </div>
                          )}
                          {adherencePct != null && (
                            <div
                              className={`font-mono-tir font-semibold text-[11px] mt-0.5 ${pctColor}`}
                              data-testid={`planner-week-adherence-${w.week_number}`}
                            >
                              {Math.round(adherencePct)}%
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {race && (() => {
                      const raceParsed = parseRaceDistance(race.distance || race.name, athletePaces);
                      return (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase font-bold text-[var(--tir-burgundy)]"
                          data-testid={`planner-race-tag-${w.week_number}`}
                          title={`${race.name} — ${race.priority || "A"}${raceParsed.km > 0 ? ` · ${raceParsed.km} km` : ""}`}
                        >
                          <Trophy size={9} />
                          Race · {race.priority || "A"}
                          {raceParsed.km > 0 && (
                            <span className="text-[var(--tir-burgundy)]/80 normal-case tracking-normal font-mono-tir">
                              · {raceParsed.km < 10 ? raceParsed.km.toFixed(1) : Math.round(raceParsed.km)}km
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-1.5 py-2">
                    <input
                      value={phase}
                      onChange={(e) =>
                        updateWeek(wi, { phase_label: e.target.value })
                      }
                      placeholder="Base / Build / Peak…"
                      data-testid={`planner-phase-${w.week_number}`}
                      className="w-full bg-transparent border-0 border-b border-transparent hover:border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs focus:outline-none"
                    />
                  </td>
                  {(w.days || []).slice(0, 7).map((day, di) => {
                    const workouts = day.workouts || [];
                    // Compute the day's actual date so we can show race tags inline
                    let dayRace = null;
                    if (ws) {
                      const base = new Date(ws + "T00:00:00");
                      base.setDate(base.getDate() + di);
                      const iso = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")}`;
                      dayRace = racesByDate[iso] || null;
                    }
                    const cellKey = `${wi}-${di}`;
                    const isDragOver = dragOver === cellKey && dragInfo && (dragInfo.weekIdx !== wi || dragInfo.dayIdx !== di);
                    return (
                      <td
                        key={day.id}
                        className={`px-1 py-1 align-top transition-colors ${
                          dayRace ? "bg-[#fdf6e7]" : ""
                        } ${isDragOver ? "bg-[var(--tir-burgundy)]/10 outline outline-2 outline-[var(--tir-burgundy)]" : ""}`}
                        data-testid={`planner-cell-${w.week_number}-${day.label}`}
                        onDragOver={(e) => {
                          if (dragInfo) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            if (dragOver !== cellKey) setDragOver(cellKey);
                          }
                        }}
                        onDragLeave={() => {
                          if (dragOver === cellKey) setDragOver(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragInfo) {
                            moveWorkout(dragInfo, { weekIdx: wi, dayIdx: di });
                          }
                          setDragInfo(null);
                          setDragOver(null);
                        }}
                      >
                        <div className="flex flex-col gap-1">
                          {dayRace && (
                            <div
                              className="border border-[var(--tir-burgundy)] bg-white px-1.5 py-1 leading-tight"
                              data-testid={`planner-race-cell-${w.week_number}-${day.label}`}
                              title={`${dayRace.name}${dayRace.priority ? " · Priority " + dayRace.priority : ""}${dayRace.distance ? " · " + dayRace.distance : ""}`}
                            >
                              <div className="flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase font-bold text-[var(--tir-burgundy)]">
                                <Trophy size={9} className="shrink-0" />
                                <span className="truncate">{dayRace.priority ? `Race · ${dayRace.priority}` : "Race"}</span>
                              </div>
                              <div className="text-[10px] font-semibold text-[var(--tir-ink)] mt-0.5 break-words leading-snug">
                                {dayRace.name}
                              </div>
                              {dayRace.distance && (
                                <div className="text-[9px] font-mono-tir text-[var(--tir-muted)] mt-0.5">
                                  {dayRace.distance}
                                </div>
                              )}
                            </div>
                          )}
                          {workouts.map((wk, wi2) => {
                            const accentColor = workoutTypeColor(wk.type);
                            const timeEst = workoutTimeEstimate(wk, athletePaces);
                            const isBeingDragged = dragInfo?.workoutId === wk.id;
                            return (
                              <div
                                key={wk.id}
                                draggable
                                onDragStart={(e) => {
                                  setDragInfo({ weekIdx: wi, dayIdx: di, workoutId: wk.id });
                                  e.dataTransfer.effectAllowed = "move";
                                  try {
                                    e.dataTransfer.setData("text/plain", wk.id);
                                  } catch {
                                    /* ignore */
                                  }
                                }}
                                onDragEnd={() => {
                                  setDragInfo(null);
                                  setDragOver(null);
                                }}
                                onDoubleClick={() => openWeekEditor(w.week_number)}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  setCtxMenu({
                                    x: e.clientX,
                                    y: e.clientY,
                                    weekIdx: wi,
                                    dayIdx: di,
                                    workoutIdx: wi2,
                                    workoutId: wk.id,
                                  });
                                }}
                                title="Drag to move · Double-click to edit · Right-click for more"
                                className={`border border-transparent hover:border-[var(--tir-border)] focus-within:border-[var(--tir-burgundy)] bg-white/70 leading-tight relative group min-w-0 overflow-hidden flex items-stretch gap-0.5 cursor-grab active:cursor-grabbing ${
                                  isBeingDragged ? "opacity-40" : ""
                                }`}
                                style={{ borderLeftColor: accentColor, borderLeftWidth: 3 }}
                                data-testid={`planner-chip-${w.week_number}-${di}-${wi2}`}
                              >
                                <div
                                  className="flex items-center text-[var(--tir-muted)]/40 group-hover:text-[var(--tir-burgundy)] px-0.5 select-none pointer-events-none"
                                  aria-hidden="true"
                                >
                                  <GripVertical size={10} />
                                </div>
                                <div className="flex-1 min-w-0 px-1 py-1">
                                <div className="flex items-start gap-1 min-w-0">
                                  <AutoGrowTextarea
                                    value={workoutShortLabel(wk)}
                                    onChange={(e) =>
                                      updateDayWorkoutText(wi, di, wi2, e.target.value)
                                    }
                                    placeholder={WORKOUT_TYPE_MAP[wk.type]?.label || "—"}
                                    autoFocus={focusKey === wk.id}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onDragStart={(e) => e.preventDefault()}
                                    data-testid={`planner-cell-input-${w.week_number}-${di}-${wi2}`}
                                    className="flex-1 min-w-0 bg-transparent text-xs focus:outline-none placeholder:text-[var(--tir-muted)]/40 placeholder:italic break-words cursor-text"
                                  />
                                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyWorkout(wk);
                                      }}
                                      className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] mt-0.5"
                                      aria-label="Copy workout"
                                      title="Copy workout"
                                      data-testid={`planner-chip-copy-${w.week_number}-${di}-${wi2}`}
                                    >
                                      <Copy size={10} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeDayWorkout(wi, di, wi2);
                                      }}
                                      className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] mt-0.5"
                                      aria-label="Remove workout"
                                      title="Remove workout"
                                      data-testid={`planner-chip-delete-${w.week_number}-${di}-${wi2}`}
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between gap-1 mt-0.5">
                                  <div className="text-[9px] tracking-[0.12em] uppercase text-[var(--tir-muted)] truncate">
                                    {WORKOUT_TYPE_MAP[wk.type]?.label || wk.type}
                                  </div>
                                  {timeEst && (
                                    <div
                                      className="text-[9px] font-mono-tir text-[var(--tir-burgundy)] whitespace-nowrap"
                                      title={
                                        athletePaces
                                          ? "Estimated from athlete's pace profile"
                                          : "Estimated at 6:30/km default — set a pace profile for personalized paces"
                                      }
                                    >
                                      {timeEst}
                                    </div>
                                  )}
                                </div>
                                </div>
                              </div>
                            );
                          })}
                          {clipWorkout && (
                            <button
                              type="button"
                              onClick={() => pasteWorkoutInto(wi, di)}
                              data-testid={`planner-paste-workout-${w.week_number}-${di}`}
                              title="Paste copied workout here"
                              className="w-full border border-dashed border-[var(--tir-burgundy)]/40 hover:border-[var(--tir-burgundy)] text-[var(--tir-burgundy)]/80 hover:text-[var(--tir-burgundy)] text-[9px] tracking-[0.2em] uppercase py-0.5 inline-flex items-center justify-center gap-0.5 transition-colors"
                            >
                              <ClipboardPaste size={9} /> paste
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => addDayWorkout(wi, di)}
                            data-testid={`planner-add-workout-${w.week_number}-${di}`}
                            className="w-full border border-dashed border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy)] text-[var(--tir-muted)]/60 text-[9px] tracking-[0.2em] uppercase py-0.5 inline-flex items-center justify-center gap-0.5 opacity-50 hover:opacity-100 transition-opacity"
                          >
                            <Plus size={9} /> {workouts.length === 0 ? "workout" : "add"}
                          </button>
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-1.5 py-2 text-right">
                    <div className="font-mono-tir text-xs text-[var(--tir-ink)]">
                      {formatUnifiedVolume(vol) || "—"}
                    </div>
                    {(vol.km > 0 || vol.minutes > 0) && (
                      <div className="mt-1 flex items-end gap-0.5 h-3 justify-end">
                        {vol.km > 0 && (
                          <div
                            className="bg-[var(--tir-burgundy)] w-2"
                            style={{ height: `${Math.max(2, (vol.km / maxKm) * 12)}px` }}
                            title={`${vol.km.toFixed(1)} km`}
                          />
                        )}
                        {vol.minutes > 0 && (
                          <div
                            className="bg-[var(--tir-navy)] w-2"
                            style={{
                              height: `${Math.max(2, (vol.minutes / maxMin) * 12)}px`,
                            }}
                            title={`${Math.round(vol.minutes)} min`}
                          />
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-1 py-2 text-center">
                    {dKm ? (
                      <div className={`font-mono-tir text-xs ${dKm.cls}`}>
                        {dKm.icon && !isRecovery && (
                          <AlertTriangle
                            size={11}
                            className="inline mr-0.5"
                            title="Volume jumped >10% — make sure this is intentional"
                          />
                        )}
                        {dKm.delta > 0 ? "+" : ""}
                        {Math.round(dKm.delta * 100)}%
                      </div>
                    ) : null}
                  </td>
                  <td className="px-1.5 py-2">
                    <AutoGrowTextarea
                      value={w.coach_note || ""}
                      onChange={(e) =>
                        updateWeek(wi, { coach_note: e.target.value })
                      }
                      placeholder="Theme, focus, nutrition cue…"
                      data-testid={`planner-note-${w.week_number}`}
                      className="w-full bg-transparent border-0 border-b border-transparent hover:border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs italic focus:outline-none break-words"
                    />
                  </td>
                  <td className="px-1.5 py-2 bg-[#FAF7F2]/40">
                    <AutoGrowTextarea
                      value={w.coach_private_note || ""}
                      onChange={(e) =>
                        updateWeek(wi, { coach_private_note: e.target.value })
                      }
                      placeholder="🔒 e.g. speed audio W5 4×90:30"
                      data-testid={`planner-private-note-${w.week_number}`}
                      title="Private to you — never shown to athlete or in email"
                      className="w-full bg-transparent border-0 border-b border-transparent hover:border-[var(--tir-navy)]/40 focus:border-[var(--tir-navy)] py-1 text-xs italic focus:outline-none break-words"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] font-semibold">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 bg-[#e8eef2] border border-[#b7d4e0]" />
          Recovery
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 bg-[#fdf6e7] border border-[#e6a35a]" />
          Race week
        </span>
        <span className="inline-flex items-center gap-1.5">
          <AlertTriangle size={11} className="text-[var(--tir-burgundy)]" />
          Volume jump &gt;10%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-2 bg-[var(--tir-burgundy)]" />
          km (direct + estimated from minutes)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-2 bg-[var(--tir-navy)]" />
          minutes (direct + estimated from km)
        </span>
      </div>

      <div className="mt-6 text-xs text-[var(--tir-muted)] italic max-w-3xl leading-relaxed">
        Tip: type things like "12K Tempo Run", "Long 21K + strides", "45 min easy", "Speed 6×400m",
        "Strength W1 D1", "Off". Drag a chip to another day, double-click to open the full editor,
        or <strong className="not-italic font-semibold text-[var(--tir-ink)]">right-click</strong> for
        Copy / Duplicate / Move to next week / Delete. Use the "Copy" link beside any week to fan a
        whole week out to similar weeks. <strong className="not-italic font-semibold text-[var(--tir-ink)]">Drag the
        W# label</strong> in the first column to reorder weeks (great for adding a recovery week mid-block).
        {athletePaces ? (
          <span>
            {" "}Time estimates use {athlete?.name}'s{" "}
            {usingLactatePaces ? (
              <span className="text-[var(--tir-burgundy)] font-semibold not-italic">lactate test paces</span>
            ) : (
              "VDOT pace profile"
            )}
            .
          </span>
        ) : (
          <span>
            {" "}Time estimates use a 6:30/km default —{" "}
            {athlete ? (
              <Link
                to={`/coaching/athletes/${athlete.id}`}
                className="underline hover:text-[var(--tir-burgundy)]"
              >
                add a race time
              </Link>
            ) : (
              <span>add a race time</span>
            )}{" "}
            to personalize paces.
          </span>
        )}
      </div>

      {/* Right-click context menu (workout chips) */}
      {ctxMenu && (() => {
        const isLast = ctxMenu.weekIdx >= (program?.weeks?.length || 0) - 1;
        // Clamp menu inside viewport (best-effort — width 200, height ~150)
        const left = Math.min(ctxMenu.x, (typeof window !== "undefined" ? window.innerWidth - 210 : ctxMenu.x));
        const top = Math.min(ctxMenu.y, (typeof window !== "undefined" ? window.innerHeight - 170 : ctxMenu.y));
        return (
          <div
            className="fixed z-50 bg-white border border-[var(--tir-ink)] shadow-xl py-1 min-w-[200px] text-[11px]"
            style={{ left, top }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
            role="menu"
            data-testid="planner-chip-context-menu"
          >
            <button
              type="button"
              role="menuitem"
              data-testid="ctx-copy-workout"
              onClick={() => {
                const w = program?.weeks?.[ctxMenu.weekIdx]?.days?.[ctxMenu.dayIdx]?.workouts?.[ctxMenu.workoutIdx];
                if (w) copyWorkout(w);
                setCtxMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 hover:bg-[var(--tir-ivory)] flex items-center gap-2 tracking-[0.12em] uppercase font-semibold text-[var(--tir-ink)]"
            >
              <Copy size={11} /> Copy
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid="ctx-duplicate-workout"
              onClick={() => {
                duplicateWorkout(ctxMenu.weekIdx, ctxMenu.dayIdx, ctxMenu.workoutIdx);
                setCtxMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 hover:bg-[var(--tir-ivory)] flex items-center gap-2 tracking-[0.12em] uppercase font-semibold text-[var(--tir-ink)]"
            >
              <Plus size={11} /> Duplicate here
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid="ctx-move-next-week"
              disabled={isLast}
              onClick={() => {
                moveToNextWeek(ctxMenu.weekIdx, ctxMenu.dayIdx, ctxMenu.workoutIdx);
                setCtxMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 hover:bg-[var(--tir-ivory)] flex items-center gap-2 tracking-[0.12em] uppercase font-semibold text-[var(--tir-ink)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              title={isLast ? "Already on the last week" : "Move to the same weekday next week"}
            >
              <CalendarRange size={11} /> Move to next week
            </button>
            <div className="my-1 border-t border-[var(--tir-border)]" />
            <button
              type="button"
              role="menuitem"
              data-testid="ctx-delete-workout"
              onClick={() => {
                removeDayWorkout(ctxMenu.weekIdx, ctxMenu.dayIdx, ctxMenu.workoutIdx);
                setCtxMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 hover:bg-[var(--tir-burgundy)]/10 flex items-center gap-2 tracking-[0.12em] uppercase font-semibold text-[var(--tir-burgundy)]"
            >
              <X size={11} /> Delete
            </button>
          </div>
        );
      })()}
    </div>
  );
}
